import React from 'react';
import { clsx } from 'clsx';
import { useWatch } from 'react-hook-form';
import type { Control, UseFormRegister, UseFormSetValue, UseFormGetValues, UseFormWatch, PathValue } from 'react-hook-form';
import type { CuppingFormData } from '../../../hooks/inventory-hooks/useCuppingProfileForm';
import { FLAVOR_HIERARCHY, type FlavorGroup } from './CuppingFormConstants';
import { useLanguage } from '../../../features/language/useLanguage';

export const RoastProfileSlider: React.FC<{
  register: UseFormRegister<CuppingFormData>;
  name: keyof CuppingFormData;
  disabled?: boolean;
}> = ({ register, name, disabled }) => {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col space-y-1 w-full max-w-xs">
      <div className="flex justify-between text-xs font-bold text-gray-700 uppercase tracking-wider">
        <span>{t('cupping.formFields.roastLevel')}</span>
      </div>
      <input 
        type="range" 
        min="1" 
        max="5"
        step="1"
        disabled={disabled}
        className={clsx(
          "w-full h-2 bg-gradient-to-r from-white via-gray-400 to-black rounded-lg appearance-none cursor-pointer border border-gray-200",
          "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full",
          "[&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:rounded-full",
          disabled 
            ? "cursor-not-allowed opacity-80 [&::-webkit-slider-thumb]:bg-gray-400 [&::-moz-range-thumb]:bg-gray-400" 
            : "[&::-webkit-slider-thumb]:bg-gray-900 [&::-moz-range-thumb]:bg-gray-900"
        )}
        {...register(name)}
      />
      <div className="flex justify-between text-[10px] text-gray-500 font-medium">
        <span>{t('cupping.formFields.light')}</span>
        <span>{t('cupping.formFields.medium')}</span>
        <span>{t('cupping.formFields.dark')}</span>
      </div>
    </div>
  );
};

export const ScoreSlider: React.FC<{
  control: Control<CuppingFormData>;
  name: keyof CuppingFormData;
  label: string;
  max: number;
  setValue?: UseFormSetValue<CuppingFormData>;
  disabled?: boolean;
  minText?: string;
  midText?: string;
  maxText?: string;
}> = ({ control, name, label, max, setValue, disabled, minText = 'Low', midText = 'Medium', maxText = 'High' }) => {
  const { t } = useLanguage();
  const watchedValue = useWatch({ control, name });
  const value = typeof watchedValue === 'number' ? watchedValue : 1;

  // Menghitung posisi persentase untuk angka di atas slider
  // Rumus: ((value - min) / (max - min)) * 100
  // If value is 0 (deleted), we clamp visual slider position to 0% (min)
  const displayValueForSlider = value < 1 ? 1 : value;
  const position = ((displayValueForSlider - 1) / (max - 1)) * 100;

  return (
    <div className="flex flex-col space-y-1 mb-2" data-test-id={`cupping-score-row-${String(name)}`}>
      <div className="flex justify-between items-end mb-4"> {/* Tambah margin bawah untuk ruang angka */}
        <label htmlFor={name} className="text-sm font-bold text-gray-800 leading-none">
          {label}
        </label>
        
        {/* Input Angka Manual */}
        <input
          type="number"
          data-test-id={`cupping-score-${String(name)}`}
          min={0}
          max={max}
          value={value === 0 ? '' : value}
          disabled={disabled}
          onChange={(e) => {
            const rawValue = e.target.value;
            
            // Handle deletion (empty string) -> set to 0
            if (rawValue === '') {
              if (setValue) {
                setValue(name, 0, { shouldValidate: true, shouldDirty: true });
              }
              return;
            }

            let val = parseInt(rawValue, 10);
            if (isNaN(val)) return;
            
            // Cap at maxScore
            if (val > max) val = max;
            if (val < 0) val = 0;
            
            if (setValue) {
               setValue(name, val, { shouldValidate: true, shouldDirty: true });
            }
          }}
          className={clsx(
            "w-12 text-center text-xs font-mono font-bold text-primary-700 bg-primary-50 px-1 py-0.5 rounded border border-primary-100 focus:ring-2 focus:ring-primary-500 outline-none",
            disabled && "cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-900 disabled:border-gray-300 disabled:opacity-100"
          )}
        />
      </div>

      <div className="relative pt-6 pb-2"> {/* Padding top ditambah untuk tempat angka */}
        
        <div 
          className="absolute top-0 flex flex-col items-center transition-all duration-75 ease-out"
          style={{ 
            left: `calc(${position}% )`,
            transform: 'translateX(-50%)' // Supaya angka tepat di tengah titik
          }}
        >
          <span className="text-[10px] font-bold text-gray-800 bg-white border border-gray-300 rounded px-1 min-w-[18px] text-center shadow-sm">
            {value}
          </span>
          {/* Panah kecil di bawah angka (opsional) */}
          <div className="w-0 h-0 border-l-[3px] border-l-transparent border-r-[3px] border-r-transparent border-t-[3px] border-t-gray-300" />
        </div>

        <input
          id={name}
          type="range"
          data-test-id={`cupping-score-slider-${String(name)}`}
          min={1}
          max={max}
          step={1}
          disabled={disabled}
          {...control.register(name, { valueAsNumber: true })}
          className={clsx(
            "w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer",
            "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full",
            "[&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:border-none [&::-moz-range-thumb]:rounded-full",
            disabled 
              ? "cursor-not-allowed opacity-100 disabled:bg-gray-300 [&::-webkit-slider-thumb]:bg-gray-500 [&::-moz-range-thumb]:bg-gray-500" 
              : "[&::-webkit-slider-thumb]:bg-gray-900 [&::-moz-range-thumb]:bg-gray-900"
          )}
        />
        
        {/* Label Bawah (Low, Medium, High) */}
        <div className="flex justify-between text-[10px] text-gray-400 font-medium mt-1 uppercase tracking-tighter">
          <span>{minText === 'Low' ? t('cupping.formFields.low') : minText}</span>
          <span>{midText === 'Medium' ? t('cupping.formFields.medium') : midText}</span>
          <span>{maxText === 'High' ? t('cupping.formFields.high') : maxText}</span>
        </div>

        {/* Garis-garis kecil (Ticks) */}
        <div className="absolute top-[28px] left-0 right-0 flex justify-between px-1 pointer-events-none opacity-30" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="w-px h-2 bg-gray-800" />
          ))}
        </div>
      </div>
    </div>
  );
};

export const CascadingCheckboxGroup: React.FC<{
  name: keyof CuppingFormData;
  register: UseFormRegister<CuppingFormData>;
  setValue: UseFormSetValue<CuppingFormData>;
  getValues: UseFormGetValues<CuppingFormData>;
  watch: UseFormWatch<CuppingFormData>;
  disabled?: boolean;
}> = ({ name, setValue, getValues, watch, disabled }) => {
  const currentValues = (watch(name) as string[]) || [];

  const handleParentChange = (groupName: string, checked: boolean) => {
    const group = FLAVOR_HIERARCHY.find(g => g.name === groupName);
    if (!group) return;

    let newValues = [...((getValues(name) as string[]) || [])];

    if (checked) {
      if (!newValues.includes(groupName)) newValues.push(groupName);
    } else {
      newValues = newValues.filter(v => v !== groupName);
      if (group.children) {
        newValues = newValues.filter(v => !group.children!.includes(v));
      }
    }
    setValue(name, newValues as unknown as PathValue<CuppingFormData, typeof name>);
  };

  const handleChildChange = (childName: string, parentName: string, checked: boolean) => {
    let newValues = [...((getValues(name) as string[]) || [])];
    
    if (checked) {
      if (!newValues.includes(childName)) newValues.push(childName);
      if (!newValues.includes(parentName)) newValues.push(parentName);
    } else {
      newValues = newValues.filter(v => v !== childName);
    }
    setValue(name, newValues as unknown as PathValue<CuppingFormData, typeof name>);
  };

  return (
    <div className="flex flex-col gap-1 text-xs">
      <div className="grid grid-cols-2 gap-x-4">
        <div className="flex flex-col gap-1">
          {FLAVOR_HIERARCHY.slice(0, Math.ceil(FLAVOR_HIERARCHY.length / 2)).map((group) => (
            <FlavorGroupItem 
              key={group.name} 
              group={group} 
              currentValues={currentValues} 
              disabled={disabled} 
              handleParentChange={handleParentChange} 
              handleChildChange={handleChildChange} 
            />
          ))}
        </div>
        <div className="flex flex-col gap-1">
          {FLAVOR_HIERARCHY.slice(Math.ceil(FLAVOR_HIERARCHY.length / 2)).map((group) => (
            <FlavorGroupItem 
              key={group.name} 
              group={group} 
              currentValues={currentValues} 
              disabled={disabled} 
              handleParentChange={handleParentChange} 
              handleChildChange={handleChildChange} 
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const FlavorGroupItem: React.FC<{
  group: FlavorGroup;
  currentValues: string[];
  disabled?: boolean;
  handleParentChange: (name: string, checked: boolean) => void;
  handleChildChange: (child: string, parent: string, checked: boolean) => void;
}> = ({ group, currentValues, disabled, handleParentChange, handleChildChange }) => {
  const isParentChecked = currentValues.includes(group.name);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className={clsx("flex items-center gap-1 font-bold cursor-pointer whitespace-nowrap", disabled ? "text-gray-800 !opacity-100" : "text-gray-800")}>
        <input
          type="checkbox"
          value={group.name}
          checked={isParentChecked}
          disabled={disabled}
          onChange={(e) => handleParentChange(group.name, e.target.checked)}
          className={clsx(
            "h-3.5 w-3.5 rounded-sm focus:ring-gray-500 border-gray-400",
            disabled ? "!opacity-100 !border-gray-500 text-gray-800 !bg-transparent checked:!text-gray-800" : "text-gray-900"
          )}
        />
        <span>{group.name}</span>
      </label>
      {group.children && (
        <div className="flex flex-wrap gap-2">
          {group.children.map((child) => {
            const isChildDisabled = disabled || !isParentChecked;
            return (
              <label key={child} className={clsx("flex items-center gap-1 cursor-pointer whitespace-nowrap", !isChildDisabled && "text-gray-600 opacity-100", isChildDisabled && "text-gray-800 font-medium !opacity-100")}>
                <input
                  type="checkbox"
                  value={child}
                  checked={currentValues.includes(child)}
                  disabled={isChildDisabled}
                  onChange={(e) => handleChildChange(child, group.name, e.target.checked)}
                  className={clsx(
                    "h-3.5 w-3.5 rounded-sm focus:ring-gray-400",
                    isChildDisabled ? "!opacity-100 !border-gray-400 text-gray-600 !bg-transparent checked:!text-gray-600 cursor-not-allowed" : "text-gray-700 border-gray-300"
                  )}
                />
                <span>{child}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const NotesField: React.FC<{
  register: UseFormRegister<CuppingFormData>;
  name: keyof CuppingFormData;
  label?: string;
  rows?: number;
  placeholder?: string;
  disabled?: boolean;
}> = ({ register, name, label, rows = 3, placeholder, disabled }) => {
  const { t } = useLanguage();
  return (
  <div className="h-full">
    <label className="block text-xs font-bold text-gray-700 mb-1">{label || t('reports.details.notes')}</label>
    <textarea
      {...register(name)}
      rows={rows}
      disabled={disabled}
      placeholder={placeholder}
      className={clsx(
        "block p-2 w-full h-[calc(100%-1.5rem)] rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs resize-none bg-yellow-50/30",
        disabled && "cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-900 disabled:border-gray-300 disabled:opacity-100 font-medium"
      )}
    />
  </div>
  );
};
